<template>
  <VDBaseTemplate>
    <div class="w-100 h-100 pb-4">
      <b-container fluid class="pt-5 mt-5">
        <h2 class="tw-mb-0 text-aegis-dk">Assay Protocols</h2>
        <p class="tw-italic tw-text-gray-700">Manage Assay Protocol labels used in SubAssay uploads (e.g. PCR, morphological).</p>
        <b-button variant="primary" class="mb-3" @click="openAdd">Add Assay Protocol</b-button>

        <b-modal
          id="assay-protocol-form-modal"
          v-model="formModalShow"
          title="Assay Protocol"
          @ok="saveForm"
          @hidden="resetForm"
        >
          <b-form-group label="Label" label-for="assay-protocol-label">
            <b-form-input
              id="assay-protocol-label"
              v-model="form.label"
              maxlength="512"
              placeholder="e.g. PCR"
            />
          </b-form-group>
        </b-modal>

        <b-modal
          id="assay-protocol-delete-modal"
          v-model="deleteModalShow"
          title="Delete Assay Protocol"
          ok-variant="danger"
          ok-title="Delete"
          @ok="confirmDelete"
        >
          <p>Delete &quot;{{ itemToDelete && itemToDelete.label }}&quot;? This may affect existing SubAssay data that references this protocol.</p>
        </b-modal>

        <b-modal
          id="api-err-modal"
          v-model="apiErrModalShow"
          title="Error"
          hide-footer
        >
          <span v-html="err_msg"></span>
          <b-button class="mt-3" block @click="apiErrModalShow = false">OK</b-button>
        </b-modal>

        <b-table
          striped
          responsive
          bordered
          head-variant="light"
          :items="items"
          :fields="fields"
          :busy="loading"
        >
          <template v-slot:cell(actions)="row">
            <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEdit(row.item)">Edit</b-button>
            <b-button size="sm" variant="outline-danger" @click="openDelete(row.item)">Delete</b-button>
          </template>
        </b-table>
        <b-button :to="{ name: 'vecdyn-datasets' }" variant="outline-primary" class="mt-3">Return to VecDyn Datasets</b-button>
      </b-container>
    </div>
  </VDBaseTemplate>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";
import VDBaseTemplate from "@/layouts/VDBaseTemplate.vue";

const API_BASE = "/api/assay-protocols";

export default {
  name: "assay-protocols",
  components: { VDBaseTemplate },
  data() {
    return {
      items: [],
      loading: false,
      formModalShow: false,
      deleteModalShow: false,
      apiErrModalShow: false,
      err_msg: "",
      form: { id: null, label: "" },
      itemToDelete: null,
      fields: [
        { key: "id", label: "ID", sortable: true },
        { key: "label", label: "Label", sortable: true },
        { key: "actions", label: "Actions", thStyle: "width: 180px" },
      ],
    };
  },
  created() {
    this.fetchList();
  },
  methods: {
    fetchList() {
      this.loading = true;
      HTTP.get(API_BASE)
        .then((res) => {
          this.items = Array.isArray(res.data) ? res.data : res.data.results || [];
        })
        .catch((err) => {
          this.err_msg = HandleErrors.HandleErrors(err);
          this.apiErrModalShow = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openAdd() {
      this.form = { id: null, label: "" };
      this.formModalShow = true;
    },
    openEdit(item) {
      this.form = { id: item.id, label: item.label || "" };
      this.formModalShow = true;
    },
    resetForm() {
      this.form = { id: null, label: "" };
    },
    saveForm(ev) {
      ev.preventDefault();
      const label = (this.form.label || "").trim();
      if (!label) {
        this.$bvModal.msgBoxOk("Label is required.", { title: "Validation" });
        return;
      }
      if (this.form.id != null) {
        HTTP.patch(`${API_BASE}/${this.form.id}/`, { label })
          .then(() => {
            this.$notify({ text: "Assay protocol updated.", type: "success", duration: 3000 });
            this.fetchList();
            this.formModalShow = false;
          })
          .catch((err) => {
            this.err_msg = this.getApiErrorMessage(err);
            this.apiErrModalShow = true;
          });
      } else {
        HTTP.post(API_BASE + "/", { label })
          .then(() => {
            this.$notify({ text: "Assay protocol created.", type: "success", duration: 3000 });
            this.fetchList();
            this.formModalShow = false;
          })
          .catch((err) => {
            this.err_msg = this.getApiErrorMessage(err);
            this.apiErrModalShow = true;
          });
      }
    },
    /**
     * Backend returns errors under "detail" only. If present, show it; otherwise use the generic fallback.
     */
    getApiErrorMessage(err) {
      const data = err.response?.data;
      if (!data) return HandleErrors.HandleErrors(err);
      const msg = data.detail;
      if (msg !== undefined && msg !== null) {
        const s = Array.isArray(msg) ? msg[0] : msg;
        if (typeof s === "string" && s.length) return s;
      }
      return HandleErrors.HandleErrors(err);
    },
    openDelete(item) {
      this.itemToDelete = item;
      this.deleteModalShow = true;
    },
    confirmDelete() {
      if (!this.itemToDelete) return;
      const id = this.itemToDelete.id;
      this.itemToDelete = null;
      HTTP.delete(`${API_BASE}/${id}/`)
        .then(() => {
          this.$notify({ text: "Assay protocol deleted.", type: "success", duration: 3000 });
          this.fetchList();
        })
        .catch((err) => {
          this.err_msg = this.getApiErrorMessage(err);
          this.apiErrModalShow = true;
        });
    },
  },
};
</script>
